<script setup lang="ts">
import { BModal } from 'bootstrap-vue-next';
import { Carousel, Slide } from 'vue3-carousel';
import dayjs from 'dayjs';
import 'vue3-carousel/dist/carousel.css';

const route = useRoute();
const id = computed(() => route.params.id as any);
const { convertAssetUrl } = useCms();
const { data, error, pending } = await useNewsItem(id);
const event = useRequestEvent();
if (event) {
  if (error.value) {
    event.node.res.statusCode = 500;
  } else if (!data.value) {
    event.node.res.statusCode = 404;
  } else {
    if (process.env['NODE_ENV'] === 'production') {
      event.node.res.setHeader(
        'Cache-Control',
        'public, max-age=180, stale-while-revalidate',
      );
    }
  }
}
const loading = useLoadingIndicator();
watch([pending], ([pending]) => {
  loading.isLoading.value = pending;
});

useSeoMeta({
  title: () => {
    if (data.value) {
      return data.value?.title as string;
    } else if (error.value) {
      return '500';
    } else {
      return '404';
    }
  },
  articleTag: () => {
    return data.value?.categories.map((x) => x.name);
  },
  ogImage: () => {
    return data.value?.featuredImage;
  },
});
// endregion

function goBack() {
  history.back();
}

function formatDate(date?: string | number | Date) {
  return date ? dayjs(date).format('YYYY . MM . DD') : '-';
}

const showPhotoModal = ref(false);
const currentSlide = ref(0);
const photoSlider = ref<any>();
</script>

<template>
  <NuxtLayout>
    <div class="container my-5">
      <button class="btn btn-link d-flex align-items-center" @click="goBack">
        <svg
          width="14"
          height="14"
          viewBox="0 0 8 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style="margin-right: 0.5rem"
        >
          <path
            d="M2.828 6.84147L7.778 12.1629L6.364 13.6829L0 6.84147L6.364 0L7.778 1.52009L2.828 6.84147Z"
            fill="currentColor"
          />
        </svg>
        <span>Back</span>
      </button>
    </div>
    <section class="news-content mb-5" v-if="data">
      <div class="container">
        <img class="featuredImage" :src="convertAssetUrl(data.featuredImage)" />
        <h1 class="pre-wrap mt-6 mb-5">{{ data.title }}</h1>
        <div class="row news-text">
          <div class="col-lg-4 mb-3">
            Date: {{ formatDate(data.date_published) }}<br />
            Category: {{ data.categories.map((x) => x.name).join(', ') }}<br />
          </div>
          <div class="col-lg-8">
            <p class="pre-wrap">{{ data.body }}</p>
          </div>
        </div>

        <div class="row g-3 g-lg-5">
          <div
            class="col-6 col-md-4"
            v-for="(photo, i) in data.photos"
            :key="i"
            tabindex="1"
            @click="
              currentSlide = i;
              showPhotoModal = true;
            "
          >
            <img
              class="photo"
              :src="convertAssetUrl(photo, { key: 'medium' })"
            />
          </div>
        </div>
      </div>
      <BModal
        v-model="showPhotoModal"
        centered
        hide-footer
        class="news-photo-gallery"
        fullscreen
        @shown="() => photoSlider?.updateSlideWidth()"
        @click.stop="showPhotoModal = false"
      >
        <template #default="{ visible }">
          <Carousel
            ref="photoSlider"
            class="photoSlider"
            v-model="currentSlide"
            v-if="visible"
          >
            <Slide
              class="gallery-slide"
              v-for="(photo, i) in data.photos"
              :key="i"
            >
              <img :src="convertAssetUrl(photo)" @click.stop />
            </Slide>
            <template #addons="{ slidesCount, currentSlide, prev, next }">
              <div class="pagination" @click.stop>
                <button type="button" @click="prev">&lt;</button>
                {{ currentSlide + 1 }} / {{ slidesCount }}
                <button type="button" @click="next">&gt;</button>
              </div>
            </template>
          </Carousel>
        </template>
      </BModal>
    </section>
    <section class="error" v-else>
      <div class="container" v-if="error">
        <h1>500</h1>
        <p class="pre-wrap">{{ error.message }}</p>
      </div>
      <div class="container" v-else>
        <Empty>
          <h1>404</h1>
          <p>Item not found</p>
        </Empty>
      </div>
    </section>
  </NuxtLayout>
</template>

<style scoped lang="scss">
@import '@/assets/css/variables.scss';
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

section.error {
  min-height: 300px;
}

.pre-wrap {
  white-space: pre-wrap;
}

h1 {
  font-size: 35px;
  font-weight: 600;
}

.news-content {
  font-size: 18px;
  font-weight: 500;

  .featuredImage {
    border-radius: 10px;
    width: 100%;
    aspect-ratio: 2 / 1;
    object-fit: cover;
    background-color: $gray-200;
  }

  .news-text {
    margin-bottom: 200px;
    @include media-breakpoint-down(lg) {
      margin-bottom: 100px;
    }
  }

  .photo {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: 10px;
    cursor: pointer;
    transition: transform 0.1s ease-out;
    background-color: $gray-200;

    &:hover {
      transform: scale(1.05);
    }
  }
}

.photoSlider {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    border-radius: 20px;
    max-height: 100%;
    max-width: 100%;
    height: auto !important; // no upscale
  }

  ::v-deep(.carousel__viewport) {
    flex: 1;

    * {
      height: 100%;
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 30px;
    color: white;

    button {
      background-color: transparent;
      border: none;
      color: white;
      font-size: 20px;
      cursor: pointer;
      padding: 16px;
    }
  }
}
</style>

<style lang="scss">
.news-photo-gallery.modal {
  --bs-modal-padding: 16px;
  --bs-modal-bg: transparent;
  --bs-modal-border-width: 0;
  --bs-modal-header-border-width: 0;

  .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
    width: 2em;
    height: 2em;
    background: transparent var(--bs-btn-close-bg) center/2em auto no-repeat;
  }
}
</style>
